<!-- Timeline Container -->
@if (items?.length) {
    <div class="relative">
        <!-- Timeline Items -->
        <div class="flex flex-col gap-0">
            @for (
                item of items;
                track item;
                let i = $index;
                let isFirst = $first;
                let isLast = $last
            ) {
                <div class="relative flex gap-4">
                    <!-- Timeline Connector -->
                    <div class="flex flex-col items-center">
                        <!-- Icon Circle -->
                        <div
                            class="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                            [ngClass]="{
                                'bg-emerald-100': isFirst,
                                'bg-slate-100': !isFirst,
                            }"
                        >
                            <i
                                class="fa-light fa-arrow-right-arrow-left"
                                [ngClass]="{
                                    'text-emerald-600': isFirst,
                                    'text-slate-400': !isFirst,
                                }"
                            ></i>
                        </div>
                        <!-- Vertical Line -->
                        @if (!isLast) {
                            <div class="w-0.5 flex-1 bg-slate-200"></div>
                        }
                    </div>
                    <!-- Content Card -->
                    <div class="flex-1 pb-6" [ngClass]="{ 'pb-0': isLast }">
                        <div
                            class="rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm"
                        >
                            <!-- Header: Action/Status Change + Time -->
                            <div
                                class="flex flex-wrap items-start justify-between gap-2"
                            >
                                <!-- Action + State Transition -->
                                <div class="flex flex-wrap items-center gap-2">
                                    <!-- Action Badge (when action field is available) -->
                                    @if (item.action) {
                                        <span
                                            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                                            [ngClass]="{
                                                'bg-emerald-50 text-emerald-700':
                                                    isFirst,
                                                'bg-primary-50 text-primary-700':
                                                    !isFirst,
                                            }"
                                        >
                                            @if (useTranslate) {
                                                {{
                                                    actionTranslationPrefix +
                                                        item.action | translate
                                                }}
                                            }
                                            @if (!useTranslate) {
                                                {{ item.action }}
                                            }
                                        </span>
                                    }
                                    <!-- State Transition (from → to) -->
                                    @if (item.fromState && item.toState) {
                                        <span
                                            class="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                                        >
                                            @if (useTranslate) {
                                                {{
                                                    translationPrefix +
                                                        item.fromState
                                                        | translate
                                                }}
                                            }
                                            @if (!useTranslate) {
                                                {{ item.fromState }}
                                            }
                                        </span>
                                        <i
                                            class="fa-light fa-arrow-left ltr:fa-arrow-right text-xs text-slate-300"
                                        ></i>
                                        <span
                                            class="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                                        >
                                            @if (useTranslate) {
                                                {{
                                                    translationPrefix +
                                                        item.toState | translate
                                                }}
                                                {{
                                                    item.toStateDisplaySuffix ||
                                                        ''
                                                }}
                                            }
                                            @if (!useTranslate) {
                                                {{ item.toState }}
                                                {{
                                                    item.toStateDisplaySuffix ||
                                                        ''
                                                }}
                                            }
                                        </span>
                                    }
                                    <!-- Fallback: Legacy display (no fromState/action fields) -->
                                    @if (!item.action && !item.fromState) {
                                        <span
                                            class="text-sm font-medium text-slate-700"
                                        >
                                            {{
                                                'translate_status_changed'
                                                    | translate
                                            }}
                                        </span>
                                        <!-- From Badge (derived from previous item) -->
                                        @if (items[i + 1]) {
                                            <span
                                                class="text-xs text-slate-400"
                                            >
                                                {{
                                                    'translate_from' | translate
                                                }}
                                            </span>
                                            <span
                                                class="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                                            >
                                                @if (useTranslate) {
                                                    {{
                                                        translationPrefix +
                                                            items[i + 1].value
                                                            | translate
                                                    }}
                                                }
                                                @if (!useTranslate) {
                                                    {{ items[i + 1].value }}
                                                }
                                            </span>
                                        }
                                        <!-- Arrow -->
                                        @if (items[i + 1]) {
                                            <i
                                                class="fa-light fa-arrow-left ltr:fa-arrow-right text-xs text-slate-300"
                                            ></i>
                                        }
                                        <!-- To Badge -->
                                        <span class="text-xs text-slate-400">
                                            {{ 'translate_to' | translate }}
                                        </span>
                                        <span
                                            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
                                            [ngClass]="{
                                                'bg-emerald-50 text-emerald-700':
                                                    isFirst,
                                                'bg-primary-50 text-primary-700':
                                                    !isFirst,
                                            }"
                                        >
                                            @if (useTranslate) {
                                                {{
                                                    translationPrefix +
                                                        item.value | translate
                                                }}
                                            }
                                            @if (!useTranslate) {
                                                {{ item.value }}
                                            }
                                        </span>
                                    }
                                </div>
                                <!-- Timestamp -->
                                <span class="text-xs text-slate-400">
                                    {{
                                        item.creationTime
                                            | date: 'yyyy-MM-dd hh:mm a'
                                    }}
                                </span>
                            </div>
                            <!-- Metadata: User & Department -->
                            @if (item.user || item.department) {
                                <div
                                    class="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500"
                                >
                                    <!-- User -->
                                    @if (item.user) {
                                        <div class="flex items-center gap-1.5">
                                            <i
                                                class="fa-light fa-user text-slate-400"
                                            ></i>
                                            <app-user-link
                                                [item]="item.user"
                                            ></app-user-link>
                                        </div>
                                    }
                                    <!-- Department -->
                                    @if (item.department) {
                                        <div class="flex items-center gap-1.5">
                                            <i
                                                class="fa-light fa-building text-slate-400"
                                            ></i>
                                            <app-department-link
                                                [item]="item.department"
                                            ></app-department-link>
                                        </div>
                                    }
                                </div>
                            }
                            <!-- Note -->
                            @if (item.note) {
                                <div
                                    class="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600"
                                >
                                    <div [innerHTML]="item.note"></div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }
        </div>
    </div>
}
